import { formatDate } from '@angular/common';
import { Component, Input } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { catchError, of, take } from 'rxjs';
import { DocksModel } from 'src/app/models/DocksModel';
import { ETransportQueryParameters, ETransportResponseDTO } from 'src/app/models/ETransportModel';
import { TrucksModel } from 'src/app/models/TrucksModel';
import { EmbalagemService } from 'src/app/services/embalagem/embalagem.service';
import { PackingPlanningService } from 'src/app/services/packing-planning/packing-planning.service';
import { DateUtilsService } from 'src/app/shared/utils/date-utils.service';

@Component({
  selector: 'app-trip-details-embalagens',
  templateUrl: './trip-details-embalagens.component.html',
  styleUrl: './trip-details-embalagens.component.css'
})
export class TripDetailsEmbalagensComponent {
  @Input() isViewOnly = false

  dateInterval: any
  updateInterval: any

  embDocks: DocksModel[] = [];
  dockId!: number;
  dockData?: DocksModel | any = undefined;
  activeDock: boolean = false;

  isLoading: boolean = false;
  pausePooling: boolean = false;
  showIntegrationForm: boolean = false;

  bgColor: string = '#FFF';

  takt: string = "";
  definedArrival?: string | null;
  definedDeparture?: string | null;
  plate: string = "";
  company?: string | null;
  remainingRealTimeFormatted?: string | null;

  currentTimeInMs = Date.now();
  currentDate = new Date(this.currentTimeInMs).toString();

  tripDBData: TrucksModel | undefined
  mkData?: ETransportResponseDTO[]

  constructor(
    private dateService: DateUtilsService,
    private embService: EmbalagemService,
    private packingService: PackingPlanningService,
    private route: ActivatedRoute,
    private router: Router) { }

  ngOnInit(): void {
    
    this.route.paramMap.subscribe(params => {
      const newDockId = Number(params.get('id'))

      if (this.dockId !== newDockId) {
        this.dockId = newDockId
        this.loadDockData(true)
      }

      this.isViewOnly = this.router.url.includes("/view")
    })
    this.updateData();
    this.dateInterval = setInterval(() => {
      if (!this.pausePooling) {
        this.updateData();
      }
    }, 5000);
  }

  toggleIntegrationForm() {
    this.showIntegrationForm = !this.showIntegrationForm
  }

  private loadDockData(showLoading: boolean = false) {
    if (showLoading) {
      this.isLoading = true
    }

    this.pausePooling = true;

    this.embService.getEmbDocksDataView().pipe
      (take(1),
        catchError(() => {
          this.pausePooling = false;
          this.isLoading = false;
          return of({ embDocks: [] })
        })
      ).subscribe(
        (response) => {
          this.embDocks = response.embDocks.map(d => ({
            ...d,
            status: new Date(d.status + 'Z')
          }));
        }
      )

    this.embService.getEmbTripsView().pipe
      (take(1),
        catchError(() => {
          this.pausePooling = false;
          this.isLoading = false;
          return of({ trucksResponse: [] });
        })
      ).subscribe(
        (response) => {
          this.tripDBData = response.trucksResponse.find(t => t.dock === this.dockId)
          this.dockData = this.embDocks.find(dock => dock.id === this.dockId);

          this.clearDisplayedData()

          this.pausePooling = false;
          this.isLoading = false;
          this.activeDock = true;
        }
      )
  }

  private updateData(): void {

    this.embService.getEmbDocksDataView().pipe
      (take(1),
        catchError(() => {
          this.pausePooling = false;
          this.isLoading = false;
          return of({ embDocks: [] })
        })
      ).subscribe(
        (response) => {
          this.embDocks = response.embDocks.map(d => ({
            ...d,
            status: new Date(d.status + 'Z')
          }));
        }
      )

    this.dockData = this.embDocks.find(dock => dock.id === this.dockId);

    if (this.shouldUpdateDockInfo()) {
      this.embService.getEmbTripsView().pipe
        (take(1),
          catchError(() => {
            return of({ trucksResponse: [] });
          })
        ).subscribe(
          (response) => {
            this.tripDBData = response.trucksResponse.find(t => t.dock === this.dockId)
          }
        )

      if (this.tripDBData?.idFisco) {
        this.checkData()
      } else {
        this.defaultTimer();
      }
    } else {
      this.resetTimer();
    }
  }

  private defaultTimer() {

    this.currentTimeInMs = Date.now()

    const statusTime = Number(this.dockData?.status);
    const standartTakt: number = 60 * 60 * 1000;

    const remainingTime: number = standartTakt + statusTime - this.currentTimeInMs;

    this.takt = this.dateService.formatTime(remainingTime);
    this.taktBackgroundColor(remainingTime, standartTakt);
    this.activeDock = true;
  }

  private resetTimer() {
    const loadingTimer = "--:--:--";
    this.activeDock = false;
    this.bgColor = "#FFF";
    this.takt = loadingTimer;
    this.tripDBData = undefined
  }

  private clearDisplayedData(): void {
    this.tripDBData = undefined;
    this.dockData = undefined;
    this.definedArrival = null;
    this.definedDeparture = null;
    this.plate = '';
    this.company = null;
    this.remainingRealTimeFormatted = null;
    this.takt = '--:--:--';
    this.bgColor = '#FFF';
    this.activeDock = false;
  }

  private shouldUpdateDockInfo() {
    return this.dockData?.truck == 1 && this.dockData.active == 1;
  }

  ngOnDestroy(): void {
    clearInterval(this.dateInterval);
  }

  checkData() {
    this.currentTimeInMs = Date.now()

    const currentTime = this.dateService.convertTimeStringToMs(this.dateService.extractTimeFromTimeStamp(Date.now()));
    const statusTime = this.dateService.convertTimeStringToMs(this.dateService.formatTimeToJsDate(this.dockData?.status));

    if (this.tripDBData?.packingPlanTrip != null && this.tripDBData?.packingPlanTrip != "") {
      this.packingService.getTripsByCodeView(this.tripDBData.packingPlanTrip).pipe
        (take(1),
          catchError(() => {
            return of({ response: [] });
          })
        ).subscribe(
          (response: any) => {
            this.definedArrival = response[0].arrivalTime;
            this.definedDeparture = response[0].departureTime;
          }
        )

      this.plate = this.tripDBData.plate!;
      this.company = this.tripDBData.supplier!;
      const taktTime = this.dateService.convertTimeStringToMs(this.dateService.calculateDifference(this.definedArrival!, this.definedDeparture!));
      const delta = this.dateService.calculateDelta(statusTime, currentTime, taktTime)

      this.remainingRealTimeFormatted = this.dateService.calculateDeltaFormatted(this.definedDeparture!)

      this.takt = formatDate(Math.abs(delta), 'HH:mm:ss', 'en-US', 'UTC');

      this.taktBackgroundColor(delta, taktTime)
    }

    else if (this.tripDBData?.packingPlanTrip == "" || this.tripDBData?.packingPlanTrip == null) {
      const standartTakt: number = 60 * 60 * 1000;
      const delta = this.dateService.calculateDelta(statusTime, currentTime, standartTakt)
      this.plate = this.tripDBData?.plate!;
      this.company = this.tripDBData?.supplier!
      this.takt = formatDate(Math.abs(delta), 'HH:mm:ss', 'en-US', 'UTC');

      if (!this.mkData) {
        this.loadMilkRunTable();
        return
      }

      this.definedArrival = this.dateService.formatTimeToJsDate(this.mkData![0].Janela!)
      const departureInMs: number = this.dateService.convertTimeStringToMs(this.definedArrival) + standartTakt

      this.definedDeparture = this.dateService.convertMsToTimeString(departureInMs)
      this.remainingRealTimeFormatted = this.dateService.calculateDeltaFormatted(this.definedDeparture)
      this.taktBackgroundColor(delta, standartTakt)
    }
    else {
      this.defaultTimer()
    }
  }

  loadMilkRunTable() {
    const request: ETransportQueryParameters = {
      table: "itemsTimeTableLCB",
      days: 1,
      date_column: "Janela",
      reference_date: this.getCurrentDateFormatted(),
      columns: null
    }
    this.packingService.getEtransportDataView(request).subscribe(response => {
      this.mkData = response;
    }, error => {
      console.error(error)
    }
    )
  }

  getCurrentDateFormatted(): string {
    const today = new Date();
    const year = today.getFullYear();
    const month = String(today.getMonth() + 1).padStart(2, '0');
    const day = String(today.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  //* -------------------------------------------- COLORAÇÃO TELA DE FUNDO  -------------------------------------------- *//

  private taktBackgroundColor(remainingTime: number, standartTakt: number) {
    if (remainingTime < 0) {
      this.bgColor = '#FF2340';
      this.takt = '-' + this.takt;
    } else if (remainingTime < (standartTakt) / 3) {
      this.bgColor = '#F1C21B';
    } else {
      this.bgColor = '#3CB371';
    }
  }

  getArrivalColor(): string {
    if (!this.dockData?.status || !this.definedArrival) return "#FFF"

    const realDate = (this.dateService.formatTimeToJsDate(new Date(this.dockData.status).toString()))
    const plannedDate = this.dateService.formatTimeToJsDate(this.dateService.convertTimeStringToValidDate(this.definedArrival)!.toString())

    if (plannedDate) {
      return realDate > plannedDate ? "#FF2340" : "#3CB371";
    } return "#FFF"
  }

  getDepartureColor(): string {
    if (!this.dockData?.status || !this.definedDeparture) return "#FFF"

    const now = new Date();
    const plannedDeparture = this.dateService.convertTimeStringToValidDate(this.definedDeparture!);

    const remaining = plannedDeparture!.getTime() - now.getTime()
    return remaining < 0 ? "#FF2340" : "#3CB371";
  }

  //* -------------------------------------------- TOGGLE -------------------------------------------- *//

  visibilityClick() {
    this.showIntegrationForm = true
  }

}





